import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, AudioLines, CircleHelp, ImagePlus, Info, Loader2, ShieldCheck, X } from "lucide-react";
import { ApiError, uploadAsset, type AssetType, type ParamSchema, type CapabilityInfo, type UploadedAsset } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Switch } from "@/components/ui/switch";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { ImageSizeControls } from "@/components/studio/StudioQuickControls";
import { Skeleton } from "@/components/ui/skeleton";
import { useCatalogOptions } from "@/hooks/useCatalogOptions";
import { isCreativeParameter } from "@/lib/capabilitySections";
import { PromptEditor } from "@/components/studio/PromptEditor";
import { referenceBindings, MENTION_TEXT_FIELDS, type ReferenceBinding } from "@/lib/mentions";
import { AssetThumb } from "@/components/media/AssetThumb";

/** Above this many static options, a select renders as a searchable combobox instead of a segmented control. */
const SELECT_SEGMENT_MAX = 8;

/**
 * Documentation-backed help for direct ModelArk image settings. Schema copy
 * wins when present; these fallbacks also cover registries seeded before the
 * descriptions were added server-side.
 */
const PARAMETER_HELP: Record<string, string> = {
  size: "Choose a supported resolution level or enter an exact WIDTHxHEIGHT value. Exact-size limits depend on the selected Seedream model.",
  response_format: "Choose a temporary image URL (valid for 24 hours) or Base64 JSON embedded in the response.",
  watermark: "Adds an “AI generated” watermark in the lower-right corner. ModelArk enables it by default for Seedream images.",
  output_format: "Choose JPEG for a smaller file or PNG for lossless output. This setting is available on supported Seedream 5.0 models.",
  sequential_image_generation: "Auto asks supported Seedream models for a related image set. Disabled returns a single image.",
  max_images: "Sets the maximum number of images returned when Image set is Auto. The cost review uses this as the maximum output count.",
  stream: "Streams image-set results as they become available. This is supported by Seedream Lite and 4.x models.",
  optimize_prompt_mode: "Controls Seedream 4.0 provider prompt optimization. Standard favors quality; Fast favors lower latency.",
};

function parameterHelp(schema: ParamSchema, modelKey: string): string | undefined {
  if (schema.description) return schema.description;
  return modelKey.startsWith("byteplus-seedream-") ? PARAMETER_HELP[schema.name] : undefined;
}

/** Normalize raw param options (plain strings or objects) into combobox options. */
function normalizeOptions(raw: ParamSchema["options"]): ComboboxOption[] {
  return (raw ?? []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
}

/** Label hint: "(default: X)" for optional params with a default, else "(optional)". */
function fieldHint(schema: ParamSchema): ReactNode {
  const hasDefault = schema.default !== undefined && schema.default !== null && schema.default !== "";
  if (schema.optional && hasDefault) {
    return (
      <span className="font-normal normal-case text-studio-faint">(default: {String(schema.default)})</span>
    );
  }
  if (schema.optional) {
    return <span className="font-normal normal-case text-studio-faint">(optional)</span>;
  }
  return undefined;
}

interface Props {
  modelKey: string;
  capability: CapabilityInfo | null;
  params: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  section?: "all" | "creative" | "technical";
  omitParamNames?: string[];
  includeParamNames?: string[];
  compact?: boolean;
}

export function CapabilityForm({
  modelKey,
  capability,
  params,
  onChange,
  section = "all",
  omitParamNames = [],
  includeParamNames,
  compact = false,
}: Props) {
  if (!capability) return null;

  // The @mention helper binds uploads into the prompt via @Image1 / @Video1 / @Audio1
  // tokens, a convention only the Seedance reference_to_video adapter interprets. Gemini
  // edit capabilities (image_to_image, image_edit_inpaint) also expose a reference_images
  // field but pass those refs as inline image parts and never parse @-tokens, so the panel
  // must be gated to the capability key — not to mere param-name presence.
  const omitted = new Set(omitParamNames);
  const included = includeParamNames ? new Set(includeParamNames) : null;
  const hasReferenceFields = ["reference_to_video", "video_edit", "video_extend"].includes(capability.key) && section !== "technical";
  const visibleParams = capability.ui_schema.params.filter(
    (param) =>
      !omitted.has(param.name) &&
      param.widget !== "camera_presets" &&
      (!included || included.has(param.name)) &&
      (section === "all" || (section === "creative" ? isCreativeParameter(param) : !isCreativeParameter(param))) &&
      (param.name !== "negative_prompt" ||
        param.native_support !== false)
  );
  const hasModelGuidance = visibleParams.some((param) => param.name === "system_instruction");

  if (visibleParams.length === 0 && !hasReferenceFields) return null;

  return (
    <div className={compact ? "space-y-4" : "space-y-5"}>
      {hasModelGuidance && (
        <div className="rounded-xl border border-studio-info-border bg-studio-info-soft p-3.5">
          <div className="flex items-start gap-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-studio-accent" />
            <div className="text-xs leading-5 text-studio-muted">
              <p><strong className="text-studio-ink">Creative prompt</strong> describes the image you want.</p>
              <p><strong className="text-studio-ink">Model guidance</strong> optionally sets Gemini&apos;s role or interpretation rules for this request.</p>
            </div>
          </div>
        </div>
      )}

      {modelKey === "byteplus-seedance-2.5" && section === "creative" && (
        <div className="rounded-xl border border-studio-warning-border bg-studio-warning-soft p-3.5">
          <div className="flex items-start gap-2.5 text-xs leading-5 text-studio-warning-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Direct uploads containing real human faces are restricted by ModelArk. Use only eligible model outputs, approved digital characters, or separately authorized portrait assets.</span>
          </div>
        </div>
      )}

      {modelKey === "byteplus-seedance-2.5" && section === "creative" && params.generate_audio !== false && (
        <div className="rounded-xl border border-studio-info-border bg-studio-info-soft p-3.5">
          <div className="flex items-start gap-2.5 text-xs leading-5 text-studio-muted">
            <AudioLines className="mt-0.5 h-4 w-4 shrink-0 text-studio-accent" />
            <span>Native audio can be rejected if it resembles copyrighted music. Describe original ambience or sound effects, avoid song, artist, or lyric references, or turn Audio off and add licensed audio afterward.</span>
          </div>
        </div>
      )}

      {visibleParams.map((p) => (
        <ParamField key={p.name} modelKey={modelKey} schema={p} value={params[p.name]} params={params} bindings={referenceBindings(params)} onChange={(v, binding) => {
          if (binding) onChange("reference_bindings", [...referenceBindings(params).filter((item) => item.token !== binding.token), binding]);
          onChange(p.name, v);
        }} />
      ))}

      {hasReferenceFields && <ReferenceTokenPanel params={params} onChange={onChange} />}
    </div>
  );
}

/** Count of uploaded refs for a field (multi → array length, single → 0/1). */
function refCount(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  return value ? 1 : 0;
}

/**
 * Seedance reference_to_video @mention helper. Renders one chip per uploaded
 * reference (@Image1…@ImageN, @Video1…, @Audio1…) that appends the token to the
 * prompt on click. Hidden until at least one reference has been uploaded.
 */
function ReferenceTokenPanel({
  params,
  onChange,
}: {
  params: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}) {
  const groups = [
    { prefix: "Image", count: refCount(params.reference_images) },
    { prefix: "Video", count: refCount(params.source_video) + refCount(params.reference_videos) },
    { prefix: "Audio", count: refCount(params.reference_audios) },
  ].filter((g) => g.count > 0);

  if (groups.length === 0) return null;

  const insertToken = (token: string) => {
    const current = typeof params.prompt === "string" ? params.prompt : "";
    const needsSpace = current.length > 0 && !/\s$/.test(current);
    onChange("prompt", current + (needsSpace ? " " : "") + token);
  };

  return (
    <div className="rounded-xl border border-studio-border bg-studio-card/50 p-3.5">
      <FieldLabel>Reference tokens</FieldLabel>
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.prefix} className="flex flex-wrap gap-1.5">
            {Array.from({ length: g.count }, (_, i) => {
              const token = `@${g.prefix}${i + 1}`;
              return (
                <button
                  key={token}
                  type="button"
                  onClick={() => insertToken(token)}
                  className="rounded-md border border-studio-border bg-studio-panel px-2 py-1 font-mono text-[11px] text-studio-muted transition-colors hover:border-studio-accent/50 hover:text-studio-ink"
                >
                  {token}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="mt-2.5 text-[11px] leading-relaxed text-studio-faint">
        Reference uploads are bound in the prompt via @Image1 / @Video1 / @Audio1 tokens.
      </p>
    </div>
  );
}

function ParameterTooltip({ label, description, paramName }: { label: string; description: string; paramName: string }) {
  const tooltipId = useId();

  return (
    <span className="group relative inline-flex normal-case tracking-normal">
      <button
        type="button"
        data-param-help={paramName}
        aria-label={`About ${label}`}
        aria-describedby={tooltipId}
        className="relative inline-flex h-5 w-5 items-center justify-center rounded-md text-studio-faint transition-colors after:absolute after:left-1/2 after:top-1/2 after:h-10 after:w-10 after:-translate-x-1/2 after:-translate-y-1/2 hover:bg-studio-card-hover hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible fixed inset-x-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[60] w-auto rounded-lg bg-studio-ink px-3 py-2 text-left text-[11px] font-normal leading-4 text-studio-bg opacity-0 shadow-panel transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 sm:absolute sm:bottom-auto sm:left-0 sm:right-auto sm:top-full sm:mt-2 sm:w-64 sm:max-w-[calc(100vw-3rem)]"
      >
        {description}
      </span>
    </span>
  );
}

function FieldLabel({ children, hint, description, paramName }: { children: ReactNode; hint?: ReactNode; description?: string; paramName?: string }) {
  const label = typeof children === "string" ? children : paramName?.replace(/_/g, " ") ?? "parameter";
  return (
    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
      {children}
      {hint}
      {description && paramName && <ParameterTooltip label={label} description={description} paramName={paramName} />}
    </div>
  );
}

/** Tiny rectangle that mirrors the actual aspect ratio. */
function RatioGlyph({ ratio, active }: { ratio: string; active: boolean }) {
  const [w, h] = ratio.split(":").map(Number);
  const box = 16;
  let bw = box;
  let bh = box;
  if (w >= h) bh = Math.max(4, Math.round((box * h) / w));
  else bw = Math.max(4, Math.round((box * w) / h));
  return (
    <span className="flex h-4 w-4 items-center justify-center">
      <span
        className={cn(
          "rounded-[2px] border transition-colors",
          active ? "border-studio-accent bg-studio-accent/30" : "border-studio-faint"
        )}
        style={{ width: bw, height: bh }}
      />
    </span>
  );
}

/** Provider-native asset reference the backend resolves (FAZ 1A). */
interface AssetRef {
  asset_id: string;
  type: string;
  mime: string;
  storage_key?: string;
  thumbnail_storage_key?: string | null;
}

function toRef(a: UploadedAsset): AssetRef {
  if (!a.asset_id) throw new Error("Upload finished without an asset id");
  return {
    asset_id: a.asset_id,
    type: a.type,
    mime: a.mime,
    storage_key: a.storage_key,
    thumbnail_storage_key: a.thumbnail_storage_key,
  };
}

function AssetRefPreview({ reference, previewUrl }: { reference: AssetRef; previewUrl?: string }) {
  return (
    <AssetThumb
      type={reference.type as AssetType}
      url={previewUrl}
      assetId={reference.asset_id}
      className={reference.type === "image" ? "text-studio-faint" : "text-studio-accent"}
      iconClassName={reference.type === "image" ? "h-4 w-4" : "h-5 w-5"}
    />
  );
}

/** Real upload widget — uploads to /api/assets/upload, previews locally via object URLs. */
function UploadField({
  modelKey,
  schema,
  value,
  onChange,
}: {
  modelKey: string;
  schema: ParamSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = schema.label ?? schema.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const multi = schema.widget === "multi_upload";
  const maxCount = multi ? schema.max ?? Infinity : 1;

  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mirror previews into a ref so we can revoke object URLs on unmount / replace.
  const previewsRef = useRef<Record<string, string>>({});
  useEffect(() => {
    previewsRef.current = previews;
  }, [previews]);
  useEffect(
    () => () => {
      Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u));
    },
    []
  );

  const refs: AssetRef[] = multi
    ? Array.isArray(value)
      ? (value as AssetRef[])
      : []
    : value
      ? [value as AssetRef]
      : [];

  const atLimit = refs.length >= maxCount;

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setUploading(true);

    try {
      if (!multi) {
        const file = fileList[0];
        const url = URL.createObjectURL(file);
        try {
          const asset = await uploadAsset(file, setUploadProgress);
          const ref = toRef(asset);
          Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u));
          setPreviews({ [ref.asset_id]: url });
          onChange(ref);
        } catch (e) {
          URL.revokeObjectURL(url);
          setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Upload failed");
        }
      } else {
        const accepted: AssetRef[] = Array.isArray(value) ? [...(value as AssetRef[])] : [];
        const added: Record<string, string> = {};
        let failed: string | null = null;
        for (const file of Array.from(fileList)) {
          if (accepted.length >= maxCount) break;
          const url = URL.createObjectURL(file);
          try {
            const asset = await uploadAsset(file, setUploadProgress);
            const ref = toRef(asset);
            accepted.push(ref);
            added[ref.asset_id] = url;
          } catch (e) {
            URL.revokeObjectURL(url);
            failed = e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Upload failed";
            break;
          }
        }
        setPreviews((p) => ({ ...p, ...added }));
        onChange(accepted.length ? accepted : undefined);
        if (failed) setError(failed);
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeAsset(assetId: string) {
    setPreviews((prev) => {
      const url = prev[assetId];
      if (url) URL.revokeObjectURL(url);
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
    if (multi) {
      const current = Array.isArray(value) ? (value as AssetRef[]) : [];
      const next = current.filter((a) => a.asset_id !== assetId);
      onChange(next.length ? next : undefined);
    } else {
      onChange(undefined);
    }
  }

  return (
    <div>
      <FieldLabel description={parameterHelp(schema, modelKey)} paramName={schema.name}>
        {label} {schema.required && <span className="text-studio-accent">*</span>}
      </FieldLabel>

      <input
        ref={inputRef}
        data-param-name={schema.name}
        type="file"
        accept={schema.mime?.join(",")}
        multiple={multi}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {refs.length > 0 && (
        <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {refs.map((ref) => (
            <div
              key={ref.asset_id}
              className="group relative aspect-square overflow-hidden rounded-lg border border-studio-border bg-studio-card"
            >
              <AssetRefPreview reference={ref} previewUrl={previews[ref.asset_id]} />
              <button
                type="button"
                onClick={() => removeAsset(ref.asset_id)}
                aria-label="Remove"
                className="touch-reveal absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-full bg-studio-bg/90 text-studio-muted opacity-0 shadow-sm transition-opacity hover:text-studio-ink group-hover:opacity-100 sm:h-8 sm:w-8"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!atLimit && (
        <button
          type="button"
          data-param-name={`${schema.name}-trigger`}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!uploading) handleFiles(e.dataTransfer.files);
          }}
          className="group flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-studio-border bg-studio-card px-4 py-7 text-center transition-all hover:border-studio-accent/50 hover:bg-studio-card-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-studio-panel text-studio-muted transition-colors group-hover:text-studio-accent">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          </span>
          <span className="text-xs font-medium text-studio-muted group-hover:text-studio-ink">
            {uploading
              ? `Uploading${uploadProgress != null ? ` ${uploadProgress}%` : "…"}`
              : `Drop ${multi && schema.max && schema.max > 1 ? `up to ${schema.max} ` : ""}${label.toLowerCase()} or click to upload`}
          </span>
          <span className="font-mono text-[10px] text-studio-faint">{schema.mime?.join(" · ") || "any format"}</span>
        </button>
      )}

      {error && <p className="mt-1.5 text-xs text-studio-danger">{error}</p>}
      {uploading && uploadProgress != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-studio-panel">
          <div className="h-full rounded-full bg-studio-accent transition-[width]" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}
    </div>
  );
}

function ParamField({
  modelKey,
  schema,
  value,
  onChange,
  bindings,
  params,
}: {
  modelKey: string;
  schema: ParamSchema;
  value: unknown;
  onChange: (v: unknown, binding?: ReferenceBinding) => void;
  bindings: ReferenceBinding[];
  params: Record<string, unknown>;
}) {
  const label = schema.label ?? schema.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const mentionable = MENTION_TEXT_FIELDS.some((name) => name === schema.name);

  switch (schema.widget) {
    case "text":
      // Seedream size: a documented ratio x level grid; typed sizes still read back.
      if (schema.name === "size" && schema.aspect_ratios?.length) {
        return (
          <div>
            <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
            <ImageSizeControls param={schema} value={value} onChange={onChange} layout="form" />
            {schema.description && (
              <p className="mt-1.5 text-[11px] leading-4 text-studio-muted">{schema.description}</p>
            )}
          </div>
        );
      }
      return (
        <div>
          <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
          <input
            data-param-name={schema.name}
            type="text"
            list={schema.options?.length ? `${schema.name}-presets` : undefined}
            value={(value as string) ?? ""}
            onChange={(event) => onChange(event.target.value)}
            placeholder={schema.options
              ?.map((option) => typeof option === "string" ? option : option.label)
              .join(", ")}
            className="h-11 w-full rounded-xl border border-studio-border bg-studio-card px-3.5 font-mono text-base outline-none transition-[border-color,box-shadow] focus:border-studio-accent/60 focus:shadow-glow-sm sm:h-10 sm:text-sm"
          />
          {schema.options?.length ? (
            <datalist id={`${schema.name}-presets`}>
              {schema.options.map((option) => {
                const normalized = typeof option === "string" ? option : option.value;
                return <option key={normalized} value={normalized} />;
              })}
            </datalist>
          ) : null}
          {schema.description && (
            <p className="mt-1.5 text-[11px] leading-4 text-studio-muted">{schema.description}</p>
          )}
        </div>
      );

    case "textarea":
      return (
        <div>
          <FieldLabel description={parameterHelp(schema, modelKey)} paramName={schema.name}>
            {label} {schema.required && <span className="text-studio-accent">*</span>}
            {schema.name === "negative_prompt" && schema.native_support === true && (
              <span className="inline-flex items-center gap-1 rounded-full bg-studio-success-soft px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-studio-success">
                <ShieldCheck className="h-3 w-3" /> Native
              </span>
            )}
          </FieldLabel>
          {schema.name === "negative_prompt" && schema.native_support === false && (
            <div className="mb-2 flex items-start gap-2 rounded-lg border border-studio-warning-border bg-studio-warning-soft px-3 py-2 text-[11px] leading-4 text-studio-warning-ink">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>This model does not natively support negative prompts. The platform adds it to the creative prompt as an avoidance instruction.</span>
            </div>
          )}
          <div className="relative">
            {mentionable ? <PromptEditor key={`${modelKey}:${schema.name}`}
              name={schema.name}
              label={label}
              value={typeof value === "string" ? value : ""}
              params={{ ...params, reference_bindings: bindings }}
              onChange={onChange}
              placeholder="Describe the result you want to create…"
              className={cn("w-full rounded-xl border border-studio-border bg-studio-card px-3.5 py-3 focus:border-studio-accent/60", schema.name === "prompt" ? "min-h-36" : "min-h-24")}
            /> : <textarea
              data-param-name={schema.name}
              value={(value as string) || ""}
              onChange={(e) => onChange(e.target.value)}
              rows={schema.name === "prompt" ? 5 : 3}
              placeholder={
                schema.name === "negative_prompt"
                  ? "What should the result avoid?"
                  : schema.name === "system_instruction"
                    ? "Example: Follow our restrained editorial art direction…"
                    : "Describe the result you want to create…"
              }
              className="w-full resize-none rounded-xl border border-studio-border bg-studio-card px-3.5 py-3 text-sm leading-relaxed outline-none transition-all placeholder:text-studio-faint focus:border-studio-accent/60 focus:shadow-glow-sm"
            />}
          </div>
          {schema.description && (
            <p className="mt-1.5 text-[11px] leading-4 text-studio-muted">{schema.description}</p>
          )}
        </div>
      );

    case "select":
      return <SelectField modelKey={modelKey} schema={schema} value={value} onChange={onChange} />;

    case "number":
      return (
        <div>
          <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
          <input
            type="number"
            min={schema.min}
            max={schema.max}
            step={schema.step}
            value={(value as number) ?? ""}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full rounded-xl border border-studio-border bg-studio-card px-3.5 py-2.5 font-mono text-sm outline-none transition-all focus:border-studio-accent/60 focus:shadow-glow-sm"
          />
        </div>
      );

    case "toggle": {
      const checked = typeof value === "boolean" ? value : Boolean(schema.default);
      return (
        <div className="flex items-center justify-between gap-3">
          <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
          <Switch checked={checked} onCheckedChange={(v) => onChange(v)} />
        </div>
      );
    }

    case "upload":
    case "multi_upload":
      return <UploadField modelKey={modelKey} schema={schema} value={value} onChange={onChange} />;

    case "hidden":
      return null;

    default:
      return null;
  }
}

/**
 * `select` widget renderer. Picks a control by option provenance + count:
 *  - aspect_ratio → dedicated ratio glyph grid (always static).
 *  - options_source set → fetch dynamic, account-scoped options (skeleton while
 *    loading); on error/empty fall back to the static `options`. Shown in a
 *    searchable Combobox (carries group/meta badges, e.g. voice gender/accent).
 *  - static, > SELECT_SEGMENT_MAX options → searchable Combobox.
 *  - static, few options → SegmentedControl (preserved, no regression).
 *
 * useCatalogOptions is called unconditionally (key is null when no source) to
 * keep hook order stable across renders.
 */
function SelectField({
  modelKey,
  schema,
  value,
  onChange,
}: {
  modelKey: string;
  schema: ParamSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = schema.label ?? schema.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const isRatio = schema.name === "aspect_ratio";
  const staticOptions = normalizeOptions(schema.options);
  const { options: dynamicOptions, isLoading } = useCatalogOptions(schema.options_source);
  const selected = (value as string) ?? null;

  // GPT Image 2.5 size: ratio + tier composer instead of a preset list.
  if (schema.name === "size" && schema.aspect_ratios?.length) {
    return (
      <div>
        <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
        <ImageSizeControls param={schema} value={value} onChange={onChange} layout="form" />
      </div>
    );
  }

  // aspect_ratio: dedicated ratio grid (always static, preserved).
  if (isRatio) {
    return (
      <div>
        <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
          {staticOptions.map((opt) => {
            const active = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={cn(
                  "flex min-h-11 flex-col items-center gap-1 rounded-lg border px-1 py-2 transition-[background-color,border-color,color]",
                  active
                    ? "border-studio-accent/50 bg-studio-accent/10"
                    : "border-studio-border bg-studio-card text-studio-muted hover:border-studio-border-light hover:text-studio-ink"
                )}
              >
                <RatioGlyph ratio={opt.value} active={active} />
                <span className={cn("font-mono text-[10px]", active && "text-studio-ink")}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Dynamic source: skeleton while loading; fall back to static on error/empty.
  if (schema.options_source) {
    if (isLoading && dynamicOptions.length === 0) {
      return (
        <div>
          <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      );
    }
    const options = dynamicOptions.length > 0 ? dynamicOptions : staticOptions;
    return (
      <div>
        <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
        <Combobox
          mono
          options={options}
          value={selected}
          onChange={onChange}
          placeholder={`Select ${label.toLowerCase()}…`}
        />
      </div>
    );
  }

  // Static with many options → searchable combobox.
  if (staticOptions.length > SELECT_SEGMENT_MAX) {
    return (
      <div>
        <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
        <Combobox
          mono
          options={staticOptions}
          value={selected}
          onChange={onChange}
          placeholder={`Select ${label.toLowerCase()}…`}
        />
      </div>
    );
  }

  // Static with few options → segmented control (preserved).
  return (
    <div>
      <FieldLabel hint={fieldHint(schema)} description={parameterHelp(schema, modelKey)} paramName={schema.name}>{label}</FieldLabel>
      <SegmentedControl mono options={staticOptions} value={selected} onChange={(v) => onChange(v)} />
    </div>
  );
}
